import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PageHeader, Panel, Stat, StatGrid } from "@/components/primitives";
import { useYard } from "@/lib/use-yard";
import { dwellTrend, throughputSeries } from "@/lib/yard-data";

export const Route = createFileRoute("/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics — PortDwellr" },
      { name: "description", content: "Throughput per hour, dwell time trend, detention exposure and bottleneck hours — what LEARN feeds back into tomorrow's queue." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AppShell>
      <AnalyticsPage />
    </AppShell>
  ),
});

function AnalyticsPage() {
  const { settings } = useYard();
  const series = throughputSeries();
  const trend = dwellTrend();
  const maxTrucks = Math.max(...series.map((s) => s.trucks));
  const maxDwell = Math.max(...series.map((s) => s.dwell));
  const peak = series.reduce((a, b) => (b.trucks > a.trucks ? b : a));
  const totalTrucks = series.reduce((a, s) => a + s.trucks, 0);
  const avgDwell = Math.round(series.reduce((a, s) => a + s.dwell, 0) / series.length);
  const maxTrendDwell = Math.max(...trend.map((t) => t.dwell));

  return (
    <>
      <PageHeader
        eyebrow="LEARN · closing the loop"
        title="Throughput & Dwell"
        lead={`LEARN studies the yard's own record and adjusts how tomorrow's queue sequences. Anything above the ${settings.freeTimeHours}h free-time line is money leaving the gate.`}
      />

      <StatGrid cols={4}>
        <Stat value={totalTrucks} label="Trucks processed today" sub="06:00 → 18:00" />
        <Stat value={`${avgDwell}m`} label="Average dwell time" sub="gate in → gate out" />
        <Stat value="14%" tone="signal" label={`Over the ${settings.freeTimeHours}h threshold`} sub="down 8 pts vs. last week" />
        <Stat value={`${peak.hour}:00`} label="Bottleneck hour" sub={`${peak.trucks} trucks in one hour`} />
      </StatGrid>

      {/* Hero chart — trucks per hour */}
      <div className="mt-10">
        <Panel title="Trucks per hour · today">
          <div className="p-5 md:p-7">
            <div className="flex h-[260px] items-end gap-[6px] border-b border-l border-line-strong pl-2">
              {series.map((s) => (
                <div key={s.hour} className="group flex h-full flex-1 flex-col justify-end">
                  <span className="mb-1 text-center font-mono text-[10px] text-steel-mid">{s.trucks}</span>
                  <span
                    className={`w-full ${s.trucks === maxTrucks ? "bg-signal" : "bg-steel"}`}
                    style={{ height: `${(s.trucks / maxTrucks) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-[6px] pl-2 pt-2">
              {series.map((s) => (
                <span key={s.hour} className="flex-1 text-center font-mono text-[10px] text-steel-mid">
                  {s.hour}
                </span>
              ))}
            </div>
            <p className="mt-5 max-w-[70ch] text-[14px] text-ink-soft">
              Peak throughput lands at {peak.hour}:00 with {peak.trucks} trucks. LEARN is pre-staging two extra
              spots ahead of that window tomorrow and shifting reefer priority to the doors with power.
            </p>
          </div>
        </Panel>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <Panel title="Average dwell by hour">
          <div className="p-5 md:p-6">
            <ul className="space-y-3">
              {series.map((s) => (
                <li key={s.hour} className="flex items-center gap-3">
                  <span className="w-[34px] font-mono text-[11px] text-steel-mid">{s.hour}</span>
                  <span className="h-[10px] flex-1 border border-line bg-paper">
                    <span
                      className={`block h-full ${s.dwell > settings.freeTimeHours * 60 ? "bg-signal" : "bg-steel-mid"}`}
                      style={{ width: `${(s.dwell / maxDwell) * 100}%` }}
                    />
                  </span>
                  <span className="w-[46px] text-right font-mono text-[11px] text-ink-soft">{s.dwell}m</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.1em] text-signal-deep">
              Amber = past the {settings.freeTimeHours}h detention-free window
            </p>
          </div>
        </Panel>

        <Panel title="Dwell & detention exposure · last 7 days">
          <div className="p-5 md:p-6">
            <div className="flex h-[180px] items-end gap-3 border-b border-l border-line-strong pl-2">
              {trend.map((t) => (
                <div key={t.day} className="flex h-full flex-1 flex-col justify-end gap-[2px]">
                  <span className="w-full bg-signal" style={{ height: `${t.overPct * 2}%` }} />
                  <span className="w-full bg-steel" style={{ height: `${(t.dwell / maxTrendDwell) * 70}%` }} />
                </div>
              ))}
            </div>
            <div className="flex gap-3 pl-2 pt-2">
              {trend.map((t) => (
                <span key={t.day} className="flex-1 text-center font-mono text-[10px] text-steel-mid">
                  {t.day}
                </span>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-5 font-mono text-[10.5px] uppercase tracking-[0.1em] text-steel-mid">
              <span className="flex items-center gap-2">
                <i className="inline-block h-[8px] w-[8px] bg-steel" /> avg dwell
              </span>
              <span className="flex items-center gap-2">
                <i className="inline-block h-[8px] w-[8px] bg-signal" /> % over threshold
              </span>
            </div>
          </div>
        </Panel>
      </div>
    </>
  );
}
